import { Copy, Icon } from '@phosphor-icons/react';
import { marked } from 'marked';
import { toast } from 'react-toastify';
import { SVG_SIZE_M, CITATION_REGEX } from 'src/constants';
import { Message, MessageType } from 'src/types';
import {
  getCodeContent,
  getHtmlContentForCopyToClipboard,
  getPlainTextContentForCopyToClipboard,
  getStringUrlListFromReferences,
  getTextContentFromMessage,
  getWebPageReferencesFromMessage,
} from 'src/utils';
import { Button } from 'src/v2/commonComponents/Button';
import { PlacesType } from 'react-tooltip';
import { ToastWithCustomIcon } from 'src/components/Toast';

type CopyMessageButtonType = 'message' | 'code';

type CopyMessageButtonProps = {
  message?: Message;
  disabled?: boolean;
  activeTab?: string;
  withTitle?: boolean;
  withBackground?: boolean;
  title?: string;
  icon?: Icon;
  type?: CopyMessageButtonType;
  tooltipPlace?: PlacesType;
  withTooltip?: boolean;
};

export const CopyMessageButton = ({
  message,
  disabled,
  activeTab,
  withTitle,
  withBackground,
  tooltipPlace = 'left',
  icon = Copy,
  title = 'Copy',
  type = 'message',
  withTooltip = true,
}: CopyMessageButtonProps) => {
  const IconComponent = icon;

  const copyMarkdownAsRichText = async (markdown: string) => {
    const htmlBody = await marked.parse(markdown);

    const styledHtml = getHtmlContentForCopyToClipboard(htmlBody);
    const plainText = getPlainTextContentForCopyToClipboard(markdown);

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([styledHtml], { type: 'text/html' }),
      }),
    ]);
  };

  const getContent = (message: Message) => {
    if (!activeTab) {
      return '';
    }

    switch (message.message_type) {
      case MessageType.CODE_TASK_CREATION_CARD:
      case MessageType.RESEARCH_TASK_CREATION_CARD:
      case MessageType.CHAT_CARD:
        return getTextContentFromMessage({ message, activeTab });

      default:
        return message.content;
    }
  };

  const handleCopyClick = async () => {
    if (!message || !activeTab) {
      return null;
    }

    const content = getContent(message);
    const updatedContent = content.replace(
      CITATION_REGEX,
      (_, number) => `[${number}]`,
    );

    const references = getWebPageReferencesFromMessage({
      message,
      activeTab,
    });

    const preparedContent =
      type === 'code' ? getCodeContent(updatedContent) : updatedContent;

    const copiedContent =
      references && type === 'message'
        ? `${preparedContent}${getStringUrlListFromReferences(references)}`
        : preparedContent;

    if (type === 'code') {
      navigator.clipboard.writeText(copiedContent);
    } else {
      await copyMarkdownAsRichText(copiedContent);
    }

    toast(<ToastWithCustomIcon message="Copied" />);
  };

  return (
    <Button
      onClick={handleCopyClick}
      disabled={disabled || !message}
      data-e2e="copy-button"
      size={withTitle ? 'medium' : 'small'}
      shape={withTitle ? 'regular' : 'round'}
      color={withBackground ? 'secondary-outline' : 'transparent'}
      tooltipContent={withTooltip ? 'Copy' : undefined}
      tooltipPlace={withTooltip ? tooltipPlace : undefined}
    >
      <IconComponent size={SVG_SIZE_M} />
      {withTitle && title}
    </Button>
  );
};
